import { useEffect, useState, useCallback } from 'react';
import { portalApi } from '../services/portalApi';
import type { SystemHealth as SystemHealthData } from '../services/portalApi';

function fmtUptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function fmtTime(d: Date): string {
  return d.toTimeString().slice(0, 8);
}

function HealthCard({ label, value, ok }: { label: string; value: string; ok: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
      <p className="text-xs text-gray-500">{label}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
        <p className="font-semibold text-sm text-gray-900">{value}</p>
      </div>
    </div>
  );
}

export default function SystemHealth() {
  const [data, setData]           = useState<SystemHealthData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = await portalApi.health();
      setData(h);
      setUnreachable(false);
      setLastChecked(new Date());
    } catch {
      setUnreachable(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const allGood = !unreachable && data?.db_status === 'ok';

  return (
    <div className="px-6 py-6 max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">System Health</h1>
          {lastChecked && (
            <p className="text-sm text-gray-400 mt-0.5">
              Checked {fmtTime(lastChecked)} · auto-refreshes every 30s
            </p>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <span className={loading ? 'animate-spin inline-block' : ''}>↻</span>
          Refresh
        </button>
      </div>

      {/* Overall banner */}
      <div className={`rounded-xl border p-4 flex items-center gap-3 ${
        allGood ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
      }`}>
        <span className={`text-xl ${allGood ? '' : ''}`}>
          {allGood ? '✅' : '❌'}
        </span>
        <span className={`font-semibold text-sm ${allGood ? 'text-green-800' : 'text-red-800'}`}>
          {unreachable
            ? 'Backend unreachable — is the server running?'
            : allGood
              ? 'All systems operational'
              : 'Degraded — database connection error'}
        </span>
      </div>

      {/* Metric cards */}
      {data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <HealthCard
            label="Backend"
            value="Online"
            ok
          />
          <HealthCard
            label="Database"
            value={data.db_status === 'ok' ? 'Connected' : 'Error'}
            ok={data.db_status === 'ok'}
          />
          <HealthCard
            label="Uptime"
            value={fmtUptime(data.uptime_s)}
            ok
          />
          <HealthCard
            label="Active patrols"
            value={String(data.active_patrols)}
            ok
          />
          <HealthCard
            label="Processes"
            value={`${data.process_count.toLocaleString()} / ${data.process_limit.toLocaleString()}`}
            ok={data.process_count / data.process_limit < 0.8}
          />
          <HealthCard
            label="Memory"
            value={`${data.memory_mb} MB`}
            ok={data.memory_mb < 1024}
          />
        </div>
      )}
    </div>
  );
}
